import { Button, Grid, Menu, MenuItem, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import _ from 'lodash';
import NestedMenuItem from 'material-ui-nested-menu-item';
import { NewCentralFormType, SpecificFilterOption } from 'modules/search/types';
import React, { useEffect, useMemo, useState } from 'react';

import { ArrowDropDown } from '@material-ui/icons';
import { getInitialSpecificFilterRowVals } from './filter-row';
import { createRowCategoryOptionsV3 } from './specific-filter-row-options-v3';
import { SearchEntityEnum } from 'modules/search/v3/search-v3-types';
import { getSearchSpaceIntersection } from 'modules/search/v3/search-v3-utils';
import { useSearchContext } from 'modules/search/context/search-context';

const intersect = <T,>(array1: T[], array2: T[]) =>
  array1.filter((value) => array2.includes(value));

const shouldBeDisabled = (
  option: SpecificFilterOption,
  checkboxes: NewCentralFormType['includeSearchEntityTypes_V3'],
  variant: 'submenu' | 'menu',
  currentSearchInList: SearchEntityEnum[]
) => {
  if (option.filterType === 'FTX') return false;

  const searchInList = option.category.validSearchSpace as SearchEntityEnum[];

  if (variant === 'submenu') {
    //if (searchInList.every((s) => !checkboxes[s])) return true;
    if (intersect(searchInList, currentSearchInList).length === 0) return true;
  } else {
    //if (searchInList.every((s) => !checkboxes[s])) return true;
    if (intersect(searchInList, currentSearchInList).length === 0) return true;
  }

  return false;
};

function FilterRowMainField({
  rowIndex,
  formField,
}: {
  rowIndex: number;
  formField: string;
}) {
  const [menuPosition, setMenuPosition] = useState<any>(null);
  const { setValidSearchSpace } = useSearchContext();

  const formikCtx = useFormikContext<NewCentralFormType>();
  const searchFilterRowOptions = createRowCategoryOptionsV3();

  const grupped = _.groupBy(
    searchFilterRowOptions,
    (o) => o.category.label
  ) as Record<string, SpecificFilterOption[]>;
  const { setFieldValue, values } = formikCtx;

  const handleRightClick = (event: React.MouseEvent) => {
    if (menuPosition) {
      return;
    }
    event.preventDefault();
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    });
  };

  const handleItemClick = (item: SpecificFilterOption) => {
    setFieldValue(formField, getInitialSpecificFilterRowVals(item));
    setMenuPosition(null);
  };

  /** 
    validSearchSpaceWithoutThisRow
    - aby sa zabranilo "konfliktu so sebou" - potrebujem najst valid search space,
    ktory je prienikom vsetkych ostatnych riadkov, okrem toho, ktory mam zrovna rozkliknuty
  */
  const validSearchSpaceWithoutThisRow = getSearchSpaceIntersection(
    values.specificFilterRows
      .filter((_, index) => index !== rowIndex)
      .map((r) => r.filterOption)
  );

  const menuState = useMemo(() => {
    const obj: { [key: string]: boolean } = {};
    Object.entries(grupped).forEach(([key, entries]) => {
      if (key !== 'undefined') {
        let someEnabled = false;
        entries.forEach((entry) => {
          const dis = shouldBeDisabled(
            entry,
            values.includeSearchEntityTypes_V3,
            'menu',
            validSearchSpaceWithoutThisRow
          );
          obj[entry.id] = dis;
          someEnabled = someEnabled || !dis;
        });

        obj[key] = !someEnabled;
      } else {
        entries.forEach((entry) => {
          const dis = shouldBeDisabled(
            entry,
            values.includeSearchEntityTypes_V3,
            'menu',
            validSearchSpaceWithoutThisRow
          );
          obj[entry.id] = dis;
        });
      }
    });
    return obj;
  }, [
    grupped,
    validSearchSpaceWithoutThisRow,
    values.includeSearchEntityTypes_V3,
  ]);

  // used to trigger update of valid search space based on filter options ids
  // (avoids to retriger it on every form vals change)
  const memOptionsIds = useMemo(
    () =>
      formikCtx.values.specificFilterRows
        .map((r) => r.filterOption?.id)
        .join(','),
    [formikCtx.values.specificFilterRows]
  );

  useEffect(() => {
    const intersection = getSearchSpaceIntersection(
      formikCtx.values.specificFilterRows.map((r) => r.filterOption)
    );
    console.log('search space: ', intersection);
    setValidSearchSpace?.(intersection);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memOptionsIds]);

  return (
    <Grid item xs={12} md={12}>
      <Menu
        open={!!menuPosition}
        onClose={() => setMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
      >
        {Object.entries(grupped).map(([key, entries]) => {
          return key !== 'undefined' ? (
            <NestedMenuItem
              label={`${key}`}
              parentMenuOpen={!!menuPosition}
              key={`nested-${key}`}
              disabled={
                formikCtx.values.specificFilterRows.length === 1
                  ? false
                  : menuState[key]
              }
            >
              {entries.map((entry, index) => {
                const disabled =
                  formikCtx.values.specificFilterRows.length === 1
                    ? false
                    : menuState[entry.id];

                return (
                  <MenuItem
                    key={`nested-menu-${entry.id}-${index}`}
                    onClick={() => handleItemClick(entry)}
                    disabled={disabled}
                  >
                    {entry.label}
                  </MenuItem>
                );
              })}
            </NestedMenuItem>
          ) : (
            <div key={`not-nested-${key}`}>
              {' '}
              {entries.map((entry) => {
                const disabled =
                  formikCtx.values.specificFilterRows.length === 1
                    ? false
                    : menuState[entry.id];
                return (
                  <MenuItem
                    key={`menu-${entry.id}`}
                    onClick={() => handleItemClick(entry)}
                    disabled={disabled}
                    style={{ ...entry.labelStyle }}
                  >
                    {entry.label}
                  </MenuItem>
                );
              })}
            </div>
          );
        })}
      </Menu>
      <Typography variant="caption">Zvolte kategorii:</Typography>
      <Button
        style={{
          width: '100%',
          textTransform: 'none',
          height: '100%',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
        variant="contained"
        onClick={handleRightClick}
      >
        <b>{values.specificFilterRows[rowIndex].filterOption.label}</b>
        <ArrowDropDown />
      </Button>
    </Grid>
  );
}

export default FilterRowMainField;
